import now from "performance-now"
import Vector from "./vector";

export const f_x0 = 0;
export const f_y0 = 1;
export const f_x1 = 2;
export const f_y1 = 3;
export const f_x2 = 4;
export const f_y2 = 5;
export const f_x3 = 6;
export const f_y3 = 7;
export const f_count = 8;
export const f_outmostEdge = 9;
export const f_size = 10;

export const g_x = 0;
export const g_y = 1;
export const g_isEdge = 2;
export const g_count = 3;
export const g_edge0 = 4;
export const g_edge1 = 5;
export const g_edge2 = 6;
export const g_edge3 = 7;
export const g_edge4 = 8;
export const g_edge5 = 9;
export const g_size = 10;


export const t_n0 = 0;
export const t_n1 = 1;
export const t_n2 = 2;
export const t_n3 = 3;
export const t_isEdge = 4;
export const t_tile0 = 5;
export const t_tile1 = 6;
export const t_tile2 = 7;
export const t_tile3 = 8;
export const t_size = 9;

const TAU = Math.PI * 2;

const SIXTH = TAU / 6;

function calculateNumberOfFaces(limit)
{
    return 6 * (limit + 1) * (limit + 1);
}


const DEFAULT_CONFIG = {
    /** coordinate width */
    width: 0,
    /** coordinate height */
    height: 0,
    /** number of rings in the hexagon / number of base intersections of hexaxgon */
    numberOfRings: 5,
    /** how many percent of the edges shall we attempt to remove? */
    removeEdges: 50,

    /** if true, the graph will be layouted a bit with every render. If false, the graph relaxation happens at creation */
    animatedEasing: true,

    /** Set to true to render the original triangles, set to "merged" to render the merged triangles before subdivision */
    renderFirstPassEdges: false,

     /** Set to true to enable some debug logging */
    debug : false,

    weightFunction : (x0,y0,x1,y1) => {
        const dx = x1 - x0;
        const dy = y1 - y0;
        return Math.sqrt(dx * dx + dy * dy);
    },

    /// MOSTLY INTERNAL CONFIG ////
    
    // calculated internally to match the height
    edgeLength: 80,
    // number of iterations until we give up (we will drop out due to having reached low energy most likely much sooner)
    maxIterations: 100,
    // set to false if the graph is done animated on animatedEasing : true
    animating: true,

    // Minimum energy at which we stop relaxing the graph
    minTension: 2
};

function updateConfig(config)
{
    config.numFaces = calculateNumberOfFaces(config.numberOfRings)
    config.firstPassLen = config.numFaces * f_size
    config.firstPassNumEdges = config.numFaces * 3
    config.edgeLength = ( Math.min(config.width, config.height) / (config.numberOfRings * 2 + 2)) | 0;
    config.animating = config.animatedEasing;
    config.relaxCount = 0;
}


function createHexagonTriangles(config)
{

    const limit = config.numberOfRings;

    //console.log("createHexagonTriangles", limit);

    const DIRECTIONS = [
        new Vector(
            Math.cos(0) * config.edgeLength,
            Math.sin(0) * config.edgeLength
        ),
        new Vector(
            Math.cos(SIXTH) * config.edgeLength,
            Math.sin(SIXTH) * config.edgeLength
        ),
        new Vector(
            Math.cos(SIXTH * 2) * config.edgeLength,
            Math.sin(SIXTH * 2) * config.edgeLength
        ),
        new Vector(
            Math.cos(SIXTH * 3) * config.edgeLength,
            Math.sin(SIXTH * 3) * config.edgeLength
        ),
        new Vector(
            Math.cos(SIXTH * 4) * config.edgeLength,
            Math.sin(SIXTH * 4) * config.edgeLength
        ),
        new Vector(
            Math.cos(SIXTH * 5) * config.edgeLength,
            Math.sin(SIXTH * 5) * config.edgeLength
        )
    ];

    const faces = new Float64Array(config.firstPassLen);

    let off = 0;

    let count = 0;
    let numTris = 1;
    do
    {
        for (let i = 0; i < 6; i++)
        {
            const v0 = DIRECTIONS[i];
            const v1 = DIRECTIONS[(i + 1) % 6];
            const v2 = DIRECTIONS[(i + 2) % 6];

            let pos = v0.copy().scale(count);

            for (let j = 0; j < numTris; j++)
            {
                if (j & 1)
                {
                    faces[off + f_x0] = (pos.x) | 0;
                    faces[off + f_y0] = (pos.y) | 0;
                    faces[off + f_x1] = (pos.x + v1.x) | 0;
                    faces[off + f_y1] = (pos.y + v1.y) | 0;
                    faces[off + f_x2] = (pos.x + v2.x) | 0;
                    faces[off + f_y2] = (pos.y + v2.y) | 0;
                    faces[off + f_count] = 3;
                    faces[off + f_outmostEdge] = -1;

                    off += f_size;

                    pos.add(v2);
                }
                else
                {

                    // All tris in the last row all have their edge #1 on the outer edge of the big hexagon
                    const isOutmost = count === limit;

                    faces[off + f_x0] = (pos.x) | 0;
                    faces[off + f_y0] = (pos.y) | 0;
                    faces[off + f_x1] = (pos.x + v0.x) | 0;
                    faces[off + f_y1] = (pos.y + v0.y) | 0;
                    faces[off + f_x2] = (pos.x + v1.x) | 0;
                    faces[off + f_y2] = (pos.y + v1.y) | 0;
                    faces[off + f_count] = 3;
                    faces[off + f_outmostEdge] = isOutmost ? 1 : -1;

                    off += f_size;
                }
            }
        }

        numTris += 2;

    } while (count++ < limit);

    return faces;
}


function findOtherEdge(faces, x0, y0, x1, y1, index, out)
{
    for (let i = 0; i < faces.length; i += f_size)
    {
        if (i === index)
        {
            continue;
        }

        // console.log("find", x0, y0, x1, y1, ":",
        //     faces[i + f_x0], faces[i + f_y0],
        //     faces[i + f_x1], faces[i + f_y1],
        //     faces[i + f_x2], faces[i + f_y2],
        //     faces[i + f_x3], faces[i + f_y3],
        // );

        const count = faces[i + f_count];
        if (
            faces[i + f_x0] === x1 && faces[i + f_y0] === y1 &&
            faces[i + f_x1] === x0 && faces[i + f_y1] === y0
        )
        {
            out.index = i;
            out.edge = 0;
            return;
        }
        if (
            faces[i + f_x1] === x1 && faces[i + f_y1] === y1 &&
            faces[i + f_x2] === x0 && faces[i + f_y2] === y0
        )
        {
            out.index = i;
            out.edge = 1;
            return;
        }

        if (count === 3)
        {
            if (
                faces[i + f_x2] === x1 && faces[i + f_y2] === y1 &&
                faces[i + f_x0] === x0 && faces[i + f_y0] === y0
            )
            {
                out.index = i;
                out.edge = 2;
                return;
            }
        }
        else
        {
            if (
                faces[i + f_x2] === x1 && faces[i + f_y2] === y1 &&
                faces[i + f_x3] === x0 && faces[i + f_y3] === y0
            )
            {
                out.index = i;
                out.edge = 2;
                return;
            }

            if (
                faces[i + f_x3] === x1 && faces[i + f_y3] === y1 &&
                faces[i + f_x0] === x0 && faces[i + f_y0] === y0
            )
            {
                out.index = i;
                out.edge = 3;
                return;
            }

        }
    }

    out.index = -1;
}


const out = {index: -1, edge: 0};


function getEdgeStack(config, faces)
{
    const length = config.numFaces * 3;
    const stack = new Int32Array(length);

    let pos = 0;
    for (let i = 0; i < config.firstPassLen; i += f_size)
    {
        const outmostEdge = faces[i + f_outmostEdge];

        const targetIsOutmostFace = outmostEdge >= 0;
        // we can't remove any of the outmost edges around the big hexagon (and we avoid having an edge triange as
        // first merged triangle to simplify edge removal
        if (!targetIsOutmostFace)
        {
            const scaled = i << 2;
            stack[pos++] = scaled ;
            stack[pos++] = scaled + 1;
            stack[pos++] = scaled + 2;
        }
    }

    // fisher-yates shuffle the stack
    for (let i  = 0; i < pos - 2; i++)
    {
        const j = i + ((Math.random() * pos - i - 1)|0)
        const tmp = stack[i];
        stack[i] = stack[j];
        stack[j] = tmp;
    }
    return stack.slice(0, pos);
}


function removeRandomEdges(config, faces)
{
    const count = (config.firstPassNumEdges * config.removeEdges / 100)|0;

    //console.log("remove attempts", count);

    const stack = getEdgeStack(config, faces);

    if (config.debug)
    {
        console.log("Shuffled stack", [ ... stack.slice() ])
    }

    let stackPos = 0;


    const eraseEdgesOfFaceInStack = index => {

        for (let i = stackPos; i < stack.length; i++)
        {
            if ((stack[i] >>> 2) === index)
            {
                stack[i] = -1;
            }
        }
    }



    let success = 0;

    // function printEdge(faces, otherIndex, outMostEdge)
    // {
    //     const count = faces[otherIndex + f_count];
    //     const x0 = faces[otherIndex + outMostEdge * 2]
    //     const y0 = faces[otherIndex + outMostEdge * 2 + 1]
    //     const x1 = outMostEdge === count -1 ? faces[otherIndex] : faces[otherIndex + (outMostEdge + 1) * 2]
    //     const y1 = outMostEdge === count -1 ? faces[otherIndex + 1] : faces[otherIndex + (outMostEdge + 1) * 2 + 1]
    //
    //
    //     return x0 + "," + y0 + "," + x1 + "," + y1;
    // }

    for (let i = 0; i < count; i++)
    {
        let code;
        do
        {
            if (stackPos === stack.length)
            {
                if (config.debug)
                {
                    config.debug && console.log("Ran out of removal candidates after successfully removing", success, "out of", count);
                }
                // no more valid removals
                return;
            }
            code = stack[stackPos++];
        } while (code === -1);


        const index = code >> 2;
        const edge = code & 3;

        const x0 = faces[index + edge * 2];
        const y0 = faces[index + edge * 2 + 1];
        const x1 = edge === 2 ? faces[index + f_x0] : faces[index + (edge + 1) * 2];
        const y1 = edge === 2 ? faces[index + f_y0] : faces[index + (edge + 1) * 2 + 1];

        findOtherEdge(faces, x0, y0, x1, y1, index, out)
        if (out.index >= 0 && faces[out.index + f_count] === 3)
        {
            const {index: otherIndex, edge: otherEdge} = out;
            const x2 = edge === 0 ? faces[index + f_x2] : faces[index + (edge - 1) * 2];
            const y2 = edge === 0 ? faces[index + f_y2] : faces[index + (edge - 1) * 2 + 1];

            // check if we're merging with an outmost face
            const outMostEdge = faces[otherIndex + 9];
            const otherIsOutmostTri = outMostEdge >= 0;

            //const before = printEdge(faces, otherIndex, outMostEdge)
            // if (otherIsOutmostTri)
            // {
            //     console.log("OUTMOST edge before split", printEdge(faces, otherIndex, outMostEdge),"EDGE CASE", otherEdge, "outMostEdge", outMostEdge)
            //     console.log("face before", faces.slice(otherIndex, otherIndex + f_size))
            // }

            faces[otherIndex + 8] = 4;
            switch (otherEdge)
            {
                case 2:
                    faces[otherIndex + f_x3] = x2;
                    faces[otherIndex + f_y3] = y2;
                    break;
                case 1:
                    faces[otherIndex + f_x3] = faces[otherIndex + f_x2];
                    faces[otherIndex + f_y3] = faces[otherIndex + f_y2];
                    faces[otherIndex + f_x2] = x2;
                    faces[otherIndex + f_y2] = y2;
                    break;
                case 0:
                    faces[otherIndex + f_x3] = faces[otherIndex + f_x2];
                    faces[otherIndex + f_y3] = faces[otherIndex + f_y2];
                    faces[otherIndex + f_x2] = faces[otherIndex + f_x1];
                    faces[otherIndex + f_y2] = faces[otherIndex + f_y1];
                    faces[otherIndex + f_x1] = x2
                    faces[otherIndex + f_y1] = y2;

                    if (otherIsOutmostTri)
                    {
                        faces[otherIndex + f_outmostEdge] = 2;
                    }

                    break;

            }
            eraseEdgesOfFaceInStack(otherIndex)

            // remove our face
            faces[index + f_count] = 0;

            success++;
        }
        eraseEdgesOfFaceInStack(index)

    }

    config.debug && console.log("Successfully removed", success, "out of", count);

    return success;
}


function calculateNumNodes(config, faces)
{
    let tris = 0;
    let quads = 0;
    for (let i = 0; i < config.firstPassLen; i += f_size)
    {
        const count = faces[i + f_count];

        if (count === 3)
        {
            tris++;
        }
        else if (count === 4)
        {
            quads++;
        }
    }

    //console.log({quads,tris})

    // we divide each quad in 9 nodes and each tri into 7 nodes
    return quads * 9 + tris * 7;
}



function connectTiles(config, tiles)
{
    const { length } = tiles;

    for (let i=0; i < length; i += t_size)
    {
        let numConnections = 0;
        for (let j = 0; j < 4; j++)
        {
            const n0 = tiles[i + t_n0 + j];
            const n1 = j === 3 ? tiles[i + t_n0 ] : tiles[i + t_n0 + j + 1];

            let otherIndex = -1;
            for (let k=0; k < tiles.length; k += t_size)
            {
                if (k === i)
                {
                    continue;
                }

                const otherNode0 = tiles[k + t_n0];
                const otherNode1 = tiles[k + t_n1];
                const otherNode2 = tiles[k + t_n2];
                const otherNode3 = tiles[k + t_n3];

                // tile edges are all defined in clockwise order, the opposite edge must necessarily walk the two nodes in
                // the opposite direction

                if (
                    otherNode1 === n0 && otherNode0 === n1 ||
                    otherNode2 === n0 && otherNode1 === n1 ||
                    otherNode3 === n0 && otherNode2 === n1 ||
                    otherNode0 === n0 && otherNode3 === n1
                )
                {

                    otherIndex = k;
                    numConnections++;
                    break;
                }
            }

            tiles[i + t_tile0 + j] = otherIndex;
        }
        tiles[i + t_isEdge] = numConnections === 4 ? 0 : 1;
    }

    config.debug && console.log("TILE GRAPH", tiles);
}


function subdivide(config, faces)
{
    const { firstPassLen } = config;

    const numNodes = calculateNumNodes(config, faces);

    const nodes = new Float64Array(numNodes * g_size);


    const tiles = new Int32Array(((numNodes / 2)|0) * t_size);

    let pos = 0;
    let tilePos = 0;

    const insertNode = (x0, y0, isEdge) => {

        x0 |= 0;
        y0 |= 0;

        for (let i = 0; i < pos; i += g_size)
        {
            if (Math.abs(nodes[i] - x0) < 4 && Math.abs(nodes[i + 1] - y0) < 4)
            {
                // if we discover an odd face vertex touching the outmost edge, we will
                // not register that because the odd tris are not marked as having an outmost edge, because they don't, they
                // only have one vertices on the edge at most
                // Later we might however return to that node within an outmost edge and we have to make sure that
                // we take over the isEdge status from such a node
                if (isEdge && !nodes[i + 2])
                {
                    nodes[i + 2] = 1;
                }

                return i;
            }
        }

        const index = pos;

        nodes[pos + g_x] = x0;
        nodes[pos + g_y] = y0;
        nodes[pos + g_isEdge] = isEdge ? 1 : 0;
        nodes[pos + g_count] = 0;

        pos += g_size;

        return index;
    }

    const addTile = ((n0,n1,n2,n3) => {

        //console.log("addTile", n0, n1, n2, n3, tilePos)

        tiles[tilePos + t_n0] = n0;
        tiles[tilePos + t_n1] = n1;
        tiles[tilePos + t_n2] = n2;
        tiles[tilePos + t_n3] = n3;

        tilePos += t_size;
    });

    const insertEdge = (n0, n1) => {
        let count = nodes[n0 + g_count];

        let found = false;
        for (let i = 0; i < count; i++)
        {
            const other = nodes[n0 + g_edge0 + i];
            if (other === n1)
            {
                found = true;
                break;
            }
        }
        if (!found)
        {
            if (count >= 6)
            {
                throw new Error("At most 6 edges per node")
            }

            nodes[n0 + g_edge0 + count++] = n1;
            nodes[n0 + g_count] = count;
        }

    }
    const connect = (n0, n1) => {

        insertEdge(n0, n1);
        insertEdge(n1, n0);

    }

    for (let i = 0; i < firstPassLen; i += f_size)
    {
        const count = faces[i + f_count];

        const x0 = faces[i + f_x0]
        const y0 = faces[i + f_y0]
        const x1 = faces[i + f_x1]
        const y1 = faces[i + f_y1]
        const x2 = faces[i + f_x2]
        const y2 = faces[i + f_y2]

        const outmostEdge = faces[i + f_outmostEdge ];

        const firstEdgeIsOutmost = outmostEdge === 1;
        const secondEdgeIsOutmost = outmostEdge === 2;

        if (count === 3)
        {
            const m0x = (x0 + x1) / 2;
            const m0y = (y0 + y1) / 2;
            const m1x = (x1 + x2) / 2;
            const m1y = (y1 + y2) / 2;
            const m2x = (x2 + x0) / 2;
            const m2y = (y2 + y0) / 2;

            const cx = (x0 + x1 + x2) / 3
            const cy = (y0 + y1 + y2) / 3

            const n0 = insertNode(x0, y0);
            const n1 = insertNode(m0x, m0y);
            const n2 = insertNode(x1, y1, firstEdgeIsOutmost);
            const n3 = insertNode(m1x, m1y, firstEdgeIsOutmost);
            const n4 = insertNode(x2, y2, firstEdgeIsOutmost);
            const n5 = insertNode(m2x, m2y);
            const n6 = insertNode(cx, cy);

            connect(n0, n1);
            connect(n1, n6);
            connect(n6, n5);
            connect(n5, n0);

            connect(n1, n2);
            connect(n2, n3);
            connect(n3, n6);
            connect(n6, n1);

            connect(n5, n6);
            connect(n6, n3);
            connect(n3, n4);
            connect(n4, n5);

            addTile(n0,n1,n6,n5);
            addTile(n1,n2,n3,n6);
            addTile(n5,n6,n3,n4);

        }
        else if (count === 4)
        {
            const x3 = faces[i + f_x3]
            const y3 = faces[i + f_y3]

            const m0x = (x0 + x1) / 2;
            const m0y = (y0 + y1) / 2;
            const m1x = (x1 + x2) / 2;
            const m1y = (y1 + y2) / 2;
            const m2x = (x2 + x3) / 2;
            const m2y = (y2 + y3) / 2;
            const m3x = (x3 + x0) / 2;
            const m3y = (y3 + y0) / 2;

            const cx = (x0 + x1 + x2 + x3) / 4
            const cy = (y0 + y1 + y2 + y3) / 4

            const n0 = insertNode(x0, y0);
            const n1 = insertNode(m0x, m0y);
            const n2 = insertNode(x1, y1, firstEdgeIsOutmost);
            const n3 = insertNode(m1x, m1y, firstEdgeIsOutmost);
            const n4 = insertNode(x2, y2, firstEdgeIsOutmost || secondEdgeIsOutmost);
            const n5 = insertNode(m2x, m2y, secondEdgeIsOutmost);
            const n6 = insertNode(x3, y3, secondEdgeIsOutmost);
            const n7 = insertNode(m3x, m3y);
            const n8 = insertNode(cx, cy);

            connect(n0, n1);
            connect(n1, n2);
            connect(n2, n3);
            connect(n3, n4);
            connect(n4, n5);
            connect(n5, n6);
            connect(n6, n7);
            connect(n7, n0);

            connect(n8, n3);
            connect(n8, n5);
            connect(n8, n7);
            connect(n8, n1);

            addTile(n0,n1,n8,n7);
            addTile(n1,n2,n3,n8);
            addTile(n8,n3,n4,n5);
            addTile(n7,n8,n5,n6);
        }
    }

    //const fillRate = (pos / g_size) / numNodes;
    //console.log("SUBDIVIDED: limit = ", numNodes, ", fill rate = ", fillRate);

    console.log("TILES: buffer = ", tiles.length, ", used = " + tilePos)

    const newTiles = tiles.slice(0, tilePos);

    connectTiles(config, newTiles);

    return [nodes.slice(0, pos), newTiles];
}


function relaxWeighted(config, graph, maxIterations = 1)
{

    const {length} = graph;

    for (let i = 0; i < maxIterations; i++)
    {
        let tension = 0;
        for (let j = 0; j < length; j += g_size)
        {
            if (!graph[j + g_isEdge])
            {
                const x0 = graph[j + g_x]
                const y0 = graph[j + g_y]
                const edgeCount = graph[j + g_count]

                let centerX = 0;
                let centerY = 0;
                let sumWeight = 0;

                for (let k = 0; k < edgeCount; k++)
                {
                    const other = graph[j + 4 + k];

                    const x1 = graph[other];
                    const y1 = graph[other + 1];

                    const weight = config.weightFunction(x0, y0, x1, y1);

                    centerX += x1 * weight;
                    centerY += y1 * weight;
                    sumWeight += weight;
                }

                const x1 = centerX / sumWeight
                const y1 = centerY / sumWeight;

                const dx = x1 - x0;
                const dy = y1 - y0;

                graph[j + g_x] = x1;
                graph[j + g_y] = y1;

                tension += dx * dx + dy * dy;

            }
        }

        if (tension < config.minTension)
        {
            config.debug && console.log("Reached minimal tension", config.minTension, "after", config.relaxCount, "iterations")
            return true;
        }
        config.relaxCount++;
    }

    if (!config.animatedEasing)
    {
        config.debug && console.log("Stopping after max iterations = " + config.maxIterations)
    }

    return false;
}


/**
 * A hexagon filled with quads forming organic shapes.
 */
class OrganicQuads {
    constructor(cfg)
    {

        const config = {
            ...DEFAULT_CONFIG,
            ...cfg
        }

        updateConfig(config)

        this.config = config;

        const faces = createHexagonTriangles(config);

        if (config.renderFirstPassEdges)
        {
            this.faces = config.renderFirstPassEdges !== "merged" ? faces.slice() : faces;
        }

        const start = config.debug && now();

        removeRandomEdges(config, faces)

        config.debug && console.log("Edge removal in " , now() - start, "ms")

        const [graph, tiles] = subdivide(config, faces);
        this.tiles = tiles;

        if (!config.animating)
        {
            relaxWeighted(config, graph, config.maxIterations);
        }
        //console.log("GRAPH f_size", graph.length / g_size, graph);
        this.graph = graph;

    }


    render = ctx =>
    {
        const { config, graph, faces } = this;


        ctx.save();

        const hw = config.width / 2;
        const hh = config.height / 2;

        ctx.translate(hw, hh)

        const {length} = graph;

        ctx.fillStyle = "#000";
        ctx.fillRect(-hw, -hh, config.width, config.height)

        if (config.renderFirstPassEdges)
        {

            // draw original quads and tris

            ctx.strokeStyle = "#f00";
            ctx.lineWidth = 1;


            let outerCount = 0;
            for (let pos = 0; pos < config.firstPassLen; pos += f_size)
            {
                const count = faces[pos + f_count];

                if (count >= 3)
                {

                    ctx.beginPath();
                    ctx.moveTo(faces[pos + f_x0 ],faces[pos + f_y0]);

                    for (let i = 1; i < count; i++)
                    {
                        ctx.lineTo(faces[pos + i*2],faces[pos +  i*2 + 1]);
                    }

                    ctx.closePath();
                    ctx.stroke();

                    const outmostEdge = faces[pos + f_outmostEdge];
                    if (outmostEdge >= 0)
                    {
                        ctx.strokeStyle = "#fe0";
                        ctx.beginPath();
                        ctx.moveTo(faces[pos + outmostEdge * 2  ],faces[pos + outmostEdge * 2 + 1]);

                        if (outmostEdge === count - 1)
                        {
                            ctx.lineTo(faces[pos + f_x0 ],faces[pos + f_y0]);
                        }
                        else
                        {
                            ctx.lineTo(faces[pos + (outmostEdge + 1 ) * 2  ],faces[pos + (outmostEdge + 1 ) * 2 + 1]);
                        }
                        ctx.stroke();
                        ctx.strokeStyle = "#f00";

                        outerCount++;

                    }

                }
            }
            //    console.log("Number of outer edges", outerCount)

            //console.log("DRAW EDGES")
        }

        // ctx.strokeStyle = "#fff";
        // ctx.lineWidth = 1;
        //
        //
        // function drawEdge(x0, y0, node)
        // {
        //     const x1 = graph[node + g_x];
        //     const y1 = graph[node + g_y];
        //
        //     ctx.beginPath();
        //     ctx.moveTo(x0, y0);
        //     ctx.lineTo(x1, y1);
        //     ctx.stroke();
        // }
        //
        //
        // for (let i = 0; i < length; i += g_size)
        // {
        //     const x0 = graph[i + g_x];
        //     const y0 = graph[i + g_y];
        //     const edgeCount = graph[i + g_count];
        //
        //     for (let j = 0; j < edgeCount; j++)
        //     {
        //         drawEdge(x0, y0, graph[i + g_edge0 + j])
        //     }
        // }

        //ctx.fillStyle = "#f0f";
        //
        // for (let i = 0; i < length; i += g_size)
        // {
        //     const x0 = graph[i + g_x];
        //     const y0 = graph[i + g_y];
        //     const isEdge = graph[i + g_isEdge];
        //
        //     if (isEdge)
        //     {
        //         ctx.fillRect(x0 - 4,  y0 - 4, 8, 8)
        //     }
        // }

        // ctx.strokeStyle = "#f00";
        // ctx.lineWidth = 4;
        //
        // for (let pos = 0; pos < config.firstPassLen; pos += f_size)
        // {
        //     const count = faces[pos + f_count];
        //     const outmostEdge = faces[pos + f_outmostEdge];
        //
        //
        //     const last = count - 1;
        //     for (let i=0; i < count; i++)
        //     {
        //         if (i === outmostEdge)
        //         {
        //             ctx.strokeStyle = "rgba(255,0,0,0.5)";
        //         }
        //         else
        //         {
        //             ctx.strokeStyle = "rgba(0,255,0,0.5)";
        //         }
        //
        //         ctx.beginPath();
        //         ctx.moveTo(faces[pos + i * 2], faces[pos + i * 2 + 1]);
        //         ctx.lineTo(
        //             i === last ? faces[pos + f_x0 ] : faces[pos + (i+1) * 2],
        //             i === last ? faces[pos + f_y0 ] : faces[pos + (i+1) * 2 + 1]
        //         );
        //         ctx.stroke();
        //     }
        // }

        ctx.restore();

        if (config.animating)
        {
            if (relaxWeighted(config, graph))
            {
                config.animating = false;
            }
            //            raf(redrawGraph)
        }
    }
}


export {  Vector }

export default OrganicQuads
